import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@design-system/components";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useCopyToClipboard, useWindowResize } from "@spiriidk/utils";
import { Check, Copy } from "lucide-react";
import React, { useCallback } from "react";

import { useDebugDataChunks } from "../hooks";

const COPY_RESET_DELAY = 3000;
const RESIZE_DEBOUNCE_DELAY = 1000;

interface DebugInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DebugInfoDialog({ open, onOpenChange }: DebugInfoDialogProps) {
  const { _ } = useLingui();
  const { copied, copy } = useCopyToClipboard({
    resetDelayMs: COPY_RESET_DELAY,
  });
  const viewport = useWindowResize({ debounceMs: RESIZE_DEBOUNCE_DELAY });

  const debugDataChunks = useDebugDataChunks({
    open,
    viewport,
  });

  const handleCopy = useCallback(() => {
    const flattened = debugDataChunks.flat();
    const data = flattened.reduce<Record<string, string>>((acc, item) => {
      acc[item.label] = item.value;
      return acc;
    }, {});

    void copy(JSON.stringify(data, null, 2));
  }, [copy, debugDataChunks]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{_(msg`Debug info`)}</DialogTitle>
          <DialogDescription className="sr-only">
            {_(msg`Debug info dialog`)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          {debugDataChunks.map((chunk, chunkIndex) => (
            <div key={`chunk-${chunkIndex}`} className="flex flex-col gap-3">
              {chunk.map((item) => (
                <div
                  key={item.label}
                  className="flex items-baseline justify-between gap-4"
                >
                  <span className="text-muted-foreground shrink-0 text-sm">
                    {item.label}
                  </span>
                  <span className="text-foreground truncate text-right text-sm font-medium">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="default"
            onClick={handleCopy}
            disabled={copied}
            className="gap-2"
          >
            {copied ? (
              <>
                <Check className="size-4" />
                {_(msg`Copied`)}
              </>
            ) : (
              <>
                <Copy className="size-4" />
                {_(msg`Copy to clipboard`)}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DebugInfoDialog };
